import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../services/supabase/supabaseClient';
import type { User, Session } from '@supabase/supabase-js';
import { identifyUser, resetAnalytics, track } from '../services/analytics';
import { getUtmFirstTouch, clearUtmFirstTouch } from '../hooks/useUtmCapture';

export interface UserProfile {
  id: string;
  display_name: string | null;
  role: 'user' | 'admin' | 'enterprise';
  organization: string | null;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  isLoading: boolean;
  isAdmin: boolean;
  signUp: (email: string, password: string) => Promise<{ error: string | null; needsConfirmation: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, display_name, role, organization, acquisition_source')
      .eq('id', userId)
      .single();

    if (error) {
      console.warn('[Auth] Could not fetch profile:', error.message);
      setProfile(null);
      return;
    }

    setProfile(data as UserProfile);

    // First-touch UTM persist: chỉ set 1 lần khi profile chưa có acquisition_source.
    // Nếu user đã có (return user, hoặc grant from organic), giữ nguyên.
    if (!(data as { acquisition_source?: string | null }).acquisition_source) {
      const utm = getUtmFirstTouch();
      if (utm?.source) {
        const { error: updateErr } = await supabase
          .from('profiles')
          .update({
            acquisition_source: utm.source,
            acquisition_medium: utm.medium,
            acquisition_campaign: utm.campaign,
            acquisition_content: utm.content,
            acquisition_term: utm.term,
            acquisition_referrer: utm.referrer,
            acquisition_landed_at: utm.landed_at,
          })
          .eq('id', userId);
        if (!updateErr) {
          track('acquisition_attributed', {
            source: utm.source,
            medium: utm.medium,
            campaign: utm.campaign,
          });
          clearUtmFirstTouch();
        } else {
          console.warn('[Auth] UTM persist failed:', updateErr.message);
        }
      }
    }
  };

  // Dedup PostHog SIGNED_IN: Supabase fires SIGNED_IN multiple times (initial load,
  // route nav re-mount AuthProvider, token refresh, tab focus, etc) → without dedup,
  // PostHog logs auth_google_success 5-10 lần per user (verified Threads cohort 2026-05-04).
  const firedSignedInUserIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        identifyUser(session.user.id, session.user.email, session.user.created_at);
      }
      setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        identifyUser(session.user.id, session.user.email, session.user.created_at);
        if (event === 'SIGNED_IN' && !firedSignedInUserIds.current.has(session.user.id)) {
          firedSignedInUserIds.current.add(session.user.id);
          track('auth_google_success', {
            user_id: session.user.id,
            provider: session.user.app_metadata?.provider,
          });
        }
      } else {
        setProfile(null);
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string): Promise<{ error: string | null; needsConfirmation: boolean }> => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) return { error: error.message, needsConfirmation: false };
    const needsConfirmation = !data.session;
    return { error: null, needsConfirmation };
  };

  const signIn = async (email: string, password: string): Promise<{ error: string | null }> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return { error: null };
  };

  const updatePassword = async (newPassword: string): Promise<{ error: string | null }> => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { error: error.message };
    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    resetAnalytics();
  };

  const isAdmin = profile?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, profile, session, isLoading, isAdmin, signUp, signIn, updatePassword, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
